/**
 * Notification Context
 * Provides notification state management and WebSocket integration
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import notificationService from '../services/NotificationService';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children, userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const unsubscribeRef = useRef(null);

  /**
   * Load initial notifications
   */
  const loadNotifications = useCallback(async (options = {}) => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const result = await notificationService.getNotifications({
        ...options,
        userId,
      });
      setNotifications(result.notifications);
      return result;
    } catch (error) {
      console.error('Failed to load notifications:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Load unread count
   */
  const loadUnreadCount = useCallback(async () => {
    if (!userId) return;

    try {
      const count = await notificationService.getUnreadCount(userId);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  }, [userId]);

  /**
   * Handle incoming WebSocket notification
   */
  const handleNewNotification = useCallback((notification) => {
    if (notification.type === 'connection') {
      setIsConnected(notification.status === 'connected');
      return;
    }

    // Add new notification to the list
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification._id,
      });
    }
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    if (!userId) return;

    try {
      await notificationService.markAsRead(notificationId, userId);
      
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Mark notification as acknowledged
   */
  const markAsAcknowledged = useCallback(async (notificationId) => {
    if (!userId) return;

    try {
      await notificationService.markAsAcknowledged(notificationId, userId);
      
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId
            ? { ...n, isAcknowledged: true, acknowledgedAt: new Date().toISOString() }
            : n
        )
      );
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      await notificationService.markAllAsRead(userId);
      
      const now = new Date().toISOString();
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true, readAt: now }))
      );
      
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    if (!userId) return;

    try {
      await notificationService.deleteNotification(notificationId, userId);
      
      setNotifications(prev => {
        const notification = prev.find(n => n._id === notificationId);
        if (notification && !notification.isRead) {
          setUnreadCount(count => Math.max(0, count - 1));
        }
        return prev.filter(n => n._id !== notificationId);
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Browser notifications not supported');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }, []);

  // Initialize WebSocket and load initial data
  useEffect(() => {
    if (!userId) return;

    // Connect WebSocket
    notificationService.connectWebSocket(userId);

    // Subscribe to WebSocket notifications
    unsubscribeRef.current = notificationService.subscribe(handleNewNotification);

    // Load initial data
    loadNotifications({ limit: 50 });
    loadUnreadCount();

    // Set up ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      notificationService.ping();
    }, 30000); // Ping every 30 seconds

    // Cleanup
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      clearInterval(pingInterval);
      notificationService.disconnectWebSocket();
    };
  }, [userId, handleNewNotification, loadNotifications, loadUnreadCount]);

  // Handle page visibility to refresh when coming back
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userId) {
        loadUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, loadUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    loadNotifications,
    loadUnreadCount,
    markAsRead,
    markAsAcknowledged,
    markAllAsRead,
    deleteNotification,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to use notification context
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export default NotificationContext;
